// Finds the hull premium-calculation / deductible default setting applicable to
// a vehicle, matched by the fleet's MAIN HULL INSURER + the full settingCriteria.
//
// Used by the AT-client add-vehicle flow to:
//  - derive the (read-only) list-price-type,
//  - decide which additional coverages are offered (those with a value),
//  - show the premium-free special-equipment amount as info.
//
// Matching mirrors determineDefaultValue (timeline/hooks/useDefaultSettings.ts):
// each criterion matches if it is UNSET (general) or contains the vehicle's
// value; a set criterion with no value provided is incompatible. The most
// SPECIFIC compatible setting wins (most criteria set+matched); a general
// (criteria-unset) setting is the fallback. Unlike the internal premium matcher,
// list-price-type is an OUTPUT here: the entered list-price selects the matching
// listPrices bracket, whose `type` becomes the derived list-price-type.

import {
  getCubicCapacityCodeByNumber,
  getMaxGrossWeightKgRangeCodeByNumber,
  getNumberOfSeatsRangeCodeByNumber,
  getWeightCapacityCodeByNumber,
} from "form/util";
import { TaxonomyState } from "store/taxonomy";
import { FleetDefaultSetting } from "types/types";

export interface HullCalcValues {
  valueType?: string;
  premiumRatePercentage?: number;
  absoulutePremiumValue?: number;
  premiumFreeSpecialEquipment?: number | string | null;
  gap?: number | string | null;
  replacementValue?: number | string | null;
  grossNegligence?: number | string | null;
  electroPlusPackage?: number | string | null;
  discountForWaiverOfTaxRefund?: number;
  [k: string]: any;
}

export interface MatchedHullSetting {
  setting: FleetDefaultSetting;
  calc: HullCalcValues;
  listPriceType: number | null;
}

/** Live vehicle form values that drive criteria matching. */
export interface HullMatchVehicleValues {
  vehicleTypeCode?: number | null;
  carUsageCode?: number | null;
  truckMountingCode?: number | null;
  powerKw?: string | number | null;
  emptyWeightKg?: string | number | null;
  weightCapacityKg?: string | number | null;
  maxGrossWeightKg?: string | number | null;
  cubicCapacityCcm?: string | number | null;
  seats?: string | number | null;
  listPrice?: string | number | null;
}

interface FindOpts {
  fleet: any;
  fleetDefaultSettings: FleetDefaultSetting[] | null;
  taxonomy: TaxonomyState;
  hullPremiumTypeId?: number;
  isTradeLicensePlate?: boolean;
  vehicleValues: HullMatchVehicleValues;
}

const parseCalc = (setting: FleetDefaultSetting): HullCalcValues => {
  try {
    return typeof setting.attributeValue === "string"
      ? JSON.parse(setting.attributeValue)
      : ((setting.attributeValue as any) || {});
  } catch {
    return {};
  }
};

// Parse a (possibly German-formatted) numeric form value: "45.000,50" -> 45000.5
const num = (v: any): number | null => {
  if (v === null || v === undefined || String(v).trim() === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const n = Number(String(v).replace(/\s/g, "").replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const inRange = (val: number, from?: number | null, to?: number | null) =>
  (from == null || val >= from) && (to == null || val <= to);

type Check = { ok: boolean; specific: boolean };
const GENERAL: Check = { ok: true, specific: false };

const arrayCrit = (arr: number[] | undefined, valueId: number | null): Check => {
  if (!arr || arr.length === 0) return GENERAL;
  if (valueId == null) return { ok: false, specific: true };
  return { ok: arr.includes(valueId), specific: true };
};

const rangeCodeCrit = (
  arr: number[] | undefined,
  raw: any,
  taxonomy: TaxonomyState,
  group: keyof TaxonomyState,
  toCode: (n: number) => string
): Check => {
  if (!arr || arr.length === 0) return GENERAL;
  const n = num(raw);
  if (n == null) return { ok: false, specific: true };
  const id = (taxonomy as any)?.[group]?.byCode?.[toCode(n)]?.id;
  return { ok: id != null && arr.includes(id), specific: true };
};

const fromToCrit = (
  ranges: { from: number; to: number }[] | undefined,
  raw: any
): Check => {
  if (!ranges || ranges.length === 0) return GENERAL;
  const n = num(raw);
  if (n == null) return { ok: false, specific: true };
  return { ok: inRange(n, ranges[0].from, ranges[0].to), specific: true };
};

// list-price bracket: type-only entries (no min/max) are general; ranged
// entries are specific and require the entered list price to fall in range.
const listPriceCrit = (
  listPrices: { type: number; min?: number; max?: number }[] | undefined,
  raw: any
): Check => {
  if (!listPrices || listPrices.length === 0) return GENERAL;
  const lp = listPrices[0];
  const hasRange = lp.min != null || lp.max != null;
  if (!hasRange) return GENERAL;
  const n = num(raw);
  if (n == null) return { ok: false, specific: true };
  return { ok: inRange(n, lp.min, lp.max), specific: true };
};

const toDate = (d: any) => {
  const t = new Date(d).getTime();
  return Number.isNaN(t) ? 0 : t;
};

/**
 * Returns the best-matching hull default setting for `attributeName`
 * ("calculation" | "deductible"), or null. Most specific compatible wins.
 */
export const findApplicableHullSetting = (
  attributeName: "calculation" | "deductible",
  { fleet, fleetDefaultSettings, taxonomy, hullPremiumTypeId, isTradeLicensePlate, vehicleValues }: FindOpts
): FleetDefaultSetting | null => {
  if (!fleetDefaultSettings || !hullPremiumTypeId) return null;
  const insurerId = Number(fleet?.mainHullInsurerPartnerNumber);
  if (!insurerId) return null;

  const base = fleetDefaultSettings.filter(
    (s) =>
      s.attributeName === attributeName &&
      s.premiumType === hullPremiumTypeId &&
      Number(s.insurerPartnerNumber) === insurerId &&
      !!s.settingCriteria?.isTradeLicensePlate === !!isTradeLicensePlate
  );
  if (base.length === 0) return null;

  const v = vehicleValues || {};
  const scored = base
    .map((s) => {
      const c: any = s.settingCriteria || {};
      const checks: Check[] = [
        arrayCrit(c.vehicleTypeCodes, v.vehicleTypeCode ?? null),
        arrayCrit(c.carUsageCodes, v.carUsageCode ?? null),
        arrayCrit(c.truckMountingCodes, v.truckMountingCode ?? null),
        rangeCodeCrit(
          c.weightCapacityKgRangeCodes,
          v.weightCapacityKg,
          taxonomy,
          "WeightCapacityKgRange",
          getWeightCapacityCodeByNumber
        ),
        rangeCodeCrit(
          c.maxGrossWeightKgRangeCodes,
          v.maxGrossWeightKg,
          taxonomy,
          "MaxGrossWeightKgRange",
          getMaxGrossWeightKgRangeCodeByNumber
        ),
        rangeCodeCrit(
          c.cubicCapacityCcmRangeCodes,
          v.cubicCapacityCcm,
          taxonomy,
          "CubicCapacityCcmRangeHg1",
          getCubicCapacityCodeByNumber
        ),
        rangeCodeCrit(
          c.numberOfSeatsRangeCodes,
          v.seats,
          taxonomy,
          "NumberOfSeatsRange",
          getNumberOfSeatsRangeCodeByNumber
        ),
        fromToCrit(c.powerKws, v.powerKw),
        fromToCrit(c.emptyWeightKgs, v.emptyWeightKg),
        listPriceCrit(c.listPrices, v.listPrice),
      ];
      if (checks.some((ch) => !ch.ok)) return null; // incompatible
      const score = checks.filter((ch) => ch.specific).length;
      return { setting: s, score, validFrom: toDate(s.validFromDate) };
    })
    .filter(Boolean) as { setting: FleetDefaultSetting; score: number; validFrom: number }[];

  if (scored.length === 0) return null;
  // Most specific first; newest validFromDate breaks ties.
  scored.sort((a, b) => b.score - a.score || b.validFrom - a.validFrom);
  return scored[0].setting;
};

export const getMatchedHullCalc = (opts: FindOpts): MatchedHullSetting | null => {
  const setting = findApplicableHullSetting("calculation", opts);
  if (!setting) return null;
  return {
    setting,
    calc: parseCalc(setting),
    listPriceType: setting.settingCriteria?.listPrices?.[0]?.type ?? null,
  };
};

/** A coverage is offered to the client iff it has a value in the setting. */
export const hasCoverageValue = (
  calc: HullCalcValues | undefined | null,
  key: "gap" | "replacementValue" | "grossNegligence" | "electroPlusPackage"
): boolean => {
  const v = calc?.[key];
  return v !== null && v !== undefined && String(v).trim() !== "";
};
